import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ProductData } from "@/mocks/products";
import SecretDoor from "./SecretDoor";

interface Props {
  product: ProductData;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ProductCTA({ product }: Props) {
  const { ref, inView } = useInView(0.1);

  return (
    <section className="relative bg-[#060508] py-48 px-6 overflow-hidden">
      {/* Center glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse, ${product.glowColor} 0%, transparent 65%)`,
          filter: "blur(80px)",
        }}
      />

      <div ref={ref} className="max-w-2xl mx-auto text-center relative z-10">
        <div
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1.8s ease, transform 1.8s ease",
          }}
        >
          {/* Ornament */}
          <div className="flex items-center justify-center gap-4 mb-16">
            <div className="w-12 h-px" style={{ background: `${product.accentColor}25` }} />
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: `${product.accentColor}50` }}
            />
            <div className="w-12 h-px" style={{ background: `${product.accentColor}25` }} />
          </div>

          <p
            className="text-white/25 text-sm tracking-[0.4em] uppercase mb-8"
            style={{ letterSpacing: "0.4em" }}
          >
            {product.kingdom}
          </p>

          <h2
            className="text-4xl md:text-5xl font-light text-white/70 mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
          >
            あなたの帰還を、
            <br />
            <span style={{ color: product.accentColor }}>待っている。</span>
          </h2>

          <p
            className="text-white/20 text-base font-light mb-16 leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.05em" }}
          >
            {product.tagline}
          </p>

          {/* Secret Door CTA */}
          <div className="mb-12">
            <SecretDoor product={product} />
          </div>

          {/* Secondary link */}
          <div>
            <Link
              to="/"
              className="cursor-pointer"
              style={{
                color: `${product.accentColor}30`,
                fontSize: "0.7rem",
                letterSpacing: "0.4em",
                textTransform: "uppercase",
                transition: "color 0.5s ease",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = `${product.accentColor}60`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = `${product.accentColor}30`;
              }}
            >
              ← すべての王国を見る
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom line */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-20"
        style={{ background: `linear-gradient(to top, transparent, ${product.accentColor}15)` }}
      />
    </section>
  );
}
